import { inject } from '@angular/core';

import {
  type IoConfig,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
  type WrappedElementProps,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementPropertiesWithData,
  type ContainerActionConfig,
  type ElementLabelProps,
  type ElementLabelStyleProps,
  NOTIFICATION_BUTTON_TYPE_OPTIONS,
  type NotificationButtonAlertConfig,
  type NotificationButtonType,
  type PropertiesConfig,
  type PropertiesConfigInputBackground,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputColumnModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME } from '@blaze-shared/schema-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { SelectKnobDataService } from '../../data/select-knob-data.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { notificationButtonV2Migration } from './migrations/002-notification-button.migration';
import { notificationButtonV3Migration } from './migrations/003-notification-button.migration';
import { notificationButtonV4PaddingToMarginMigration } from './migrations/004-notification-button-padding-to-margin.migration';
import { NotificationButtonComponent } from './notification-button.component';
import { NotificationButtonPropsExtractor } from './notification-button.props-extractor';

export type NotificationButtonDef = WithProps<NotificationButtonPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    notificationButton: NotificationButtonDef;
  }
}

const DEFAULT_WIDTH = 168;
const DEFAULT_HEIGHT = 40;

registerElementDefinition({
  type: 'notificationButton',
  componentType: NotificationButtonComponent,
  isListed: true,
  name: 'Notification Button',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsExtractor: NotificationButtonPropsExtractor,
  defaults: getDefaults(),
  toolbar: ['triggerClick'],
  tags: ['Button', 'Notification'],
  groups: ['advanced'],
  styleIncludePaths: ['style'],
  version: 4,
  migrations: {
    2: notificationButtonV2Migration,
    3: notificationButtonV3Migration,
    4: notificationButtonV4PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'click', schema: null }],
  };
}

type PropsModel = CanvasElementPropertiesWithData &
  WrappedElementProps &
  StyleConfigProps &
  VisibilityElementProps;

export type NotificationButtonPropsModel = PropsModel & {
  content: {
    title: ElementLabelProps;
    columns: PropertiesConfigInputColumnModel[];
    fields: {
      isUnread?: string;
    };
    notificationButton: {
      type?: NotificationButtonType;
      icon?: string;
      /**
       * Moved to title props.
       * @deprecated
       */
      label?: string;
    };
    action?: ContainerActionConfig;
    alert?: NotificationButtonAlertConfig;
    font: {
      fontFamily: string;
      fontSize: ValueWithUnitOfMeasure;
      lineHeight: ValueWithUnitOfMeasure;
    };
  };
  style: {
    general: {
      title: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    label: {
      counter: PropertiesConfigInputColorModel;
      label: PropertiesConfigInputColorModel;
    };
    labelBlock: ElementLabelStyleProps;
    button: PropertiesConfigInputBackground;
  };
};

function getDefaults(): PropsOf<'notificationButton'> {
  return {
    content: {
      title: {
        label: 'Label',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      columns: [],
      fields: {},
      notificationButton: {
        type: 'icon_with_counter',
        icon: 'notification',
      },
      action: undefined,
      alert: undefined,
      visible: undefined,
      font: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        lineHeight: '$t.lineHeight',
      },
    },
    style: {
      general: {
        title: { color1: '$t.value', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      label: {
        counter: { color1: '$t.accentLabel', selectorType: 'solid' },
        label: { color1: '$t.value', selectorType: 'solid' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      button: {
        fill: { color1: '$t.accent', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'none' },
        outlineWidth: '1px',
        radius: '25px',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('notificationButton', [
    epc.section('content', 'Content', [
      epc.subsections.subsection('data', 'Source', [
        epc.fields.selectDataSourceWithAlert('data.?key:value.dataSource', ''),
        ...epc.utils.disabledFields(
          [
            epc.fields.listFieldsField('content.columns', 'data.?key:value.dataSource'),
            epc.utils.observedField(
              epc.fields.selectFieldFromModel(
                'content.fields.isUnread',
                'Notify when',
                'content.columns',
                {
                  showSelection: true,
                  allowNone: true,
                  labelField: 'name',
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('checkbox'),
                },
              ),
              (
                _model: NotificationButtonPropsModel,
                value: string,
                _syncModel: any,
                elementId: string | undefined,
              ) => {
                if (!elementId) {
                  return {};
                }
                const selectKnobDataService: SelectKnobDataService = inject(SelectKnobDataService);
                selectKnobDataService.updateDataSourceFieldWithAlert(elementId, value, null);
                return {};
              },
            ),
          ],
          (model) =>
            !getValueByPath(model, 'data.?key:value.dataSource')
              ? 'The Value of the element is not bound'
              : undefined,
        ),
      ]),
      epc.subsections.subsection('content.notificationButton', 'Options', [
        epc.utils.observedField(
          epc.fields.selectFieldFromList(
            'content.notificationButton.type',
            'Alert type',
            NOTIFICATION_BUTTON_TYPE_OPTIONS,
            { showSelection: true, allowNone: false },
          ),
          (_model: NotificationButtonPropsModel, value: NotificationButtonType) => {
            return value === 'icon_with_counter' ? { 'style.button.radius': '25px' } : {};
          },
        ),
        ...epc.utils.conditionalFields(
          [epc.fields.iconField('content.notificationButton.icon')],
          'content.notificationButton.type',
          (type: NotificationButtonType) => type !== 'icon_with_counter',
        ),
      ]),

      epc.subsections.subsection(
        'content',
        'Action',
        [epc.fields.buttonActionField('content.action')],
        { hideLabel: true },
      ),

      epc.subsections.visibilitySubsection(),

      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),
        epc.subsections.subsection('style.label', 'Label', [
          epc.fields.colorField('style.label.counter', 'Counter', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),
        epc.subsections.elementBackgroundSubsection('style.button', 'Button', {
          hideEffectPanel: true,
        }),
        epc.subsections.labelStyleSubsection({
          color: 'style.general.title',
          enabled: 'content.title.enabled',
          fontFamily: 'style.labelBlock.fontFamily',
          fontSize: 'style.labelBlock.fontSize',
          fontStyle: 'style.labelBlock.fontStyle',
          fontWeight: 'style.labelBlock.fontWeight',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.labelBlock.iconSize',
          lineHeight: 'style.labelBlock.lineHeight',
        }),
      ],
      true,
    ),
  ]);
}
